/** Phase 6: Admin console Waitlist management — search, delete, CSV export. */
import { useEffect, useState, useCallback, useMemo } from "react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { Inbox, Search, Trash2, Download } from "lucide-react";
import { formatDateTimestamp } from "../lib/time";
import { T } from "../lib/i18n";

export default function WaitlistPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/waitlist");
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      `${r.first_name} ${r.last_name} ${r.email} ${r.company} ${r.event_type} ${r.comments}`.toLowerCase().includes(s));
  }, [rows, q]);

  const del = async (id) => {
    try {
      await api.delete(`/admin/waitlist/${id}`);
      setRows((r) => r.filter((x) => x.id !== id));
      toast.success("Removed from waitlist");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/waitlist/export", { responseType: "blob" });
      const u = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = u; a.download = "venuefy-waitlist.csv"; a.click();
      URL.revokeObjectURL(u);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const fmtTs = (iso) => formatDateTimestamp(iso) || iso;

  return (
    <div className="card-elev p-5" data-testid="waitlist-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Inbox className="w-4 h-4 text-primary-motif" />
        <div className="font-display text-xl">{T("Waitlist")} <span className="text-sm text-motif-muted">({rows.length})</span></div>
        <button onClick={exportCsv} className="btn-ghost text-xs ml-auto flex items-center gap-1.5" data-testid="waitlist-export">
          <Download className="w-3 h-3" /> Export CSV
        </button>
      </div>
      <div className="relative mb-3">
        <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-motif-muted pointer-events-none" />
        <input className="input-motif" style={{ paddingLeft: "2.25rem" }} placeholder="Search name, email, company, event type…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="waitlist-search" />
      </div>
      {filtered.length === 0 ? (
        <div className="text-xs text-motif-muted py-6 text-center" data-testid="waitlist-empty">
          {rows.length === 0 ? "No waitlist signups yet — they'll appear here as people join from the public site." : "No matches for your search."}
        </div>
      ) : (
        <div className="max-h-[50vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] tracking-luxe text-motif-muted sticky top-0 bg-motif">
              <tr className="border-b border-motif">
                <th className="text-left py-2">{T("Name")}</th><th className="text-left">{T("Email")}</th>
                <th className="text-left hidden md:table-cell">{T("Event")}</th>
                <th className="text-left hidden md:table-cell">{T("Guests")}</th>
                <th className="text-left">{T("When")}</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-motif align-top" data-testid={`waitlist-row-${r.id}`}>
                  <td className="py-2 pr-2">
                    <div>{r.first_name} {r.last_name}</div>
                    {r.company && <div className="text-[10px] text-motif-muted">{r.company}</div>}
                  </td>
                  <td className="pr-2 break-all">{r.email}</td>
                  <td className="pr-2 hidden md:table-cell">{r.event_type || "—"}</td>
                  <td className="pr-2 hidden md:table-cell">{r.estimated_guests || "—"}</td>
                  <td className="pr-2 text-xs text-motif-muted whitespace-nowrap">{fmtTs(r.created_at)}</td>
                  <td className="text-right">
                    <button onClick={() => del(r.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-red-500" title="Delete" data-testid={`waitlist-delete-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
